import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import CustomMultiSelect from "./CustomMultiSelect";
import { buildFundingOptions } from "./fundingData";

const ID = "funding";

const FundingDropdown = observer(props => {
  const { discoverPage = false } = props || {};
  const initialOptions = useMemo(() => {
    const opts = buildFundingOptions();
    return opts.map(cat => ({
      ...cat,
      submenu: (cat.submenu || []).map(s => ({ ...s, show: true }))
    }));
  }, []);

  const syncCheckedFromFunding = (opts, funding = []) => {
    const list = opts || initialOptions;
    if (!Array.isArray(funding) || funding.length === 0) {
      return list.map(cat => ({
        ...cat,
        checked: false,
        indeterminate: false,
        submenu: (cat.submenu || []).map(s => ({ ...s, checked: false }))
      }));
    }
    const set = new Set(funding);
    return list.map(cat => {
      const submenu = (cat.submenu || []).map(s => ({
        ...s,
        checked: set.has(s.id)
      }));
      const checkedCount = submenu.filter(s => s.checked).length;
      const allChecked = submenu.length > 0 && checkedCount === submenu.length;
      const indeterminate = submenu.length > 0 && checkedCount > 0 && !allChecked;
      return { ...cat, checked: allChecked, indeterminate, submenu };
    });
  };

  const [options, setOptions] = useState(() =>
    syncCheckedFromFunding(initialOptions, dataSearchState.filterComponentData?.[ID])
  );

  const handleApplySelection = (tempData = options) => {
    const funding = [];
    tempData.forEach(item => {
      if (item?.id === "selectAll") return;
      (item?.submenu || []).forEach(sub => {
        if (sub?.checked) funding.push(sub.id);
      });
    });
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [ID]: funding
    });
  };

  const handleSelectActionCbk = (checked, uId, name) => {
    let tempData = options.map(item => {
      if (item?.id !== uId) return item;
      const submenu = (item.submenu || []).map(s => ({ ...s, checked }));
      const indeterminate = !checked && submenu.some(s => s?.checked);
      return { ...item, checked, indeterminate, submenu };
    });
    setOptions(tempData);
    handleApplySelection(tempData);
  };

  const handleSubmenuSelectActionCbk = (checked, uId, name, itemWithSubmenu) => {
    let tempData = options.map(item => {
      if (item?.id !== itemWithSubmenu?.id) return item;
      const submenu = (item.submenu || []).map(s =>
        s?.id === uId ? { ...s, checked } : s
      );
      const checkedCount = submenu.filter(s => s.checked).length;
      const allChecked = submenu.length > 0 && checkedCount === submenu.length;
      const indeterminate = submenu.length > 0 && checkedCount > 0 && !allChecked;
      return { ...item, submenu, checked: allChecked, indeterminate };
    });
    setOptions(tempData);
    handleApplySelection(tempData);
  };

  const searchFilterCbk = value => {
    const lower = (value || "").toLowerCase().trim();
    const funding = dataSearchState.filterComponentData?.[ID] || [];
    if (!lower) {
      const synced = syncCheckedFromFunding(initialOptions, funding);
      setOptions(
        synced.map(cat => ({
          ...cat,
          show: true,
          submenu: (cat.submenu || []).map(s => ({ ...s, show: true }))
        }))
      );
      return;
    }
    const withChecked = syncCheckedFromFunding(initialOptions, funding);
    setOptions(
      withChecked.map(cat => {
        const catMatch = (cat.value || cat.label || "")
          .toLowerCase()
          .includes(lower);
        const submenu = (cat.submenu || []).map(s => {
          const match = (s.value || s.name || "")
            .toLowerCase()
            .includes(lower);
          return { ...s, show: match };
        });
        const anySub = submenu.some(s => s.show);
        return {
          ...cat,
          show: catMatch || anySub,
          submenu
        };
      })
    );
  };

  const resetDropdownToInitialState = () => {
    const funding = dataSearchState.filterComponentData?.[ID] || [];
    const synced = syncCheckedFromFunding(initialOptions, funding);
    setOptions(
      synced.map(cat => ({
        ...cat,
        show: true,
        submenu: (cat.submenu || []).map(s => ({ ...s, show: true }))
      }))
    );
  };

  const resetFilterOptions = () => {
    const funding = dataSearchState.filterComponentData?.[ID] || [];
    const synced = syncCheckedFromFunding(initialOptions, funding);
    setOptions(
      synced.map(cat => ({
        ...cat,
        show: true,
        submenu: (cat.submenu || []).map(s => ({ ...s, show: true }))
      }))
    );
  };

  const resetOptions = () => {
    const synced = syncCheckedFromFunding(initialOptions, []);
    setOptions(synced);
  };

  useEffect(() => {
    if (discoverPage) {
      resetFilterOptions();
    }
  }, [dataSearchState.filterComponentData]);

  useEffect(() => {
    if (dataSearchState.clearAll) {
      resetOptions();
      dataSearchState.setClearAll(false);
    }
  }, [dataSearchState.clearAll]);

  return (
    <CustomMultiSelect
      id={ID}
      options={options}
      handleSelectActionCbk={handleSelectActionCbk}
      handleSubmenuSelectActionCbk={handleSubmenuSelectActionCbk}
      handleApplySelectionCbk={() => {
        handleApplySelection(options);
        resetDropdownToInitialState();
      }}
      searchFilterCbk={searchFilterCbk}
      hasSubMenuAccordion={true}
      resetDropdownToInitialState={resetDropdownToInitialState}
      resetFilterOptions={resetFilterOptions}
      discoverPage={discoverPage}
      onCloseDropdownOnly={props.onCloseDropdownOnly}
      {...props}
      showSearchPanel={false}
    />
  );
});

export { FundingDropdown };
export default FundingDropdown;
